// publish-87 round-11 · Post to TikTok (Direct Post UX required by TikTok's
// Content Sharing Guidelines for the video.publish scope).
//
//  1. creator_info/query BEFORE the post screen → nickname + avatar
//  2. privacy picker from privacy_level_options, NO default
//  3. Allow Comment / Duet / Stitch off by default, greyed when the creator
//     has them turned off (comment_disabled / duet_disabled / stitch_disabled)
//  4. max_video_post_duration_sec enforced (client hint + server check)
//  5. Commercial content disclosure (off by default) → "Your brand" /
//     "Branded content"; branded content cannot be "Only me"
//  6. Consent line above Publish with Music Usage Confirmation (+ Branded
//     Content Policy when branded content is on) links
//  7. Plain <video> preview — nothing is drawn over the creator's video
//  8. Publish only on explicit click → poll status/fetch, "may take a few
//     minutes" note
//  9. Direct post (video/init + post_info) when video.publish is granted,
//     inbox (video.upload) fallback otherwise — decided server-side.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const MUSIC_USAGE_URL = "https://www.tiktok.com/legal/page/global/music-usage-confirmation/en";
const BC_POLICY_URL = "https://www.tiktok.com/legal/page/global/bc-policy/en";
const PRIVATE_BLOCKED_TEXT = "Branded content visibility cannot be set to private";
const DISCLOSURE_REQUIRED_TEXT =
  "You need to indicate if your content promotes yourself, a third party, or both";
const POLL_MS = 4000;

const STATUS_TONE = {
  PUBLISH_COMPLETE: "success",
  FAILED: "danger",
  PROCESSING_UPLOAD: "warn",
  PROCESSING_DOWNLOAD: "warn",
  SEND_TO_USER_INBOX: "success",
};

function fmtSeconds(s) {
  if (s === null || s === undefined) return "—";
  const n = Math.round(Number(s));
  return n >= 60 ? `${Math.floor(n / 60)}m ${n % 60}s` : `${n}s`;
}

function Section({ step, title, children, testId }) {
  return (
    <section className="bo-card" data-testid={testId} style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <span className="bo-eyebrow">Step {step}</span>
        <h2 style={{ margin: 0, fontSize: 17 }}>{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Toggle({ id, label, checked, onChange, disabled, hint, testId }) {
  return (
    <label
      htmlFor={id}
      title={disabled ? hint : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
        borderRadius: "var(--bo-radius-sm)", border: "1px solid var(--bo-line)",
        opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <input
        id={id} type="checkbox" role="switch" aria-checked={checked}
        checked={checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        style={{ width: 18, height: 18, accentColor: "var(--bo-accent)" }}
      />
      <span style={{ display: "grid" }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{label}</span>
        {hint && <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>{hint}</span>}
      </span>
    </label>
  );
}

export default function TikTokDirectPost() {
  const [connections, setConnections] = useState(null);
  const [connectionId, setConnectionId] = useState("");
  const [creator, setCreator] = useState(null);
  const [creatorError, setCreatorError] = useState(null);
  const [sources, setSources] = useState(null);
  const [sourceId, setSourceId] = useState("");
  const [title, setTitle] = useState("");
  const [privacy, setPrivacy] = useState("");           // NO default
  const [allowComment, setAllowComment] = useState(false);
  const [allowDuet, setAllowDuet] = useState(false);
  const [allowStitch, setAllowStitch] = useState(false);
  const [disclosure, setDisclosure] = useState(false);
  const [yourBrand, setYourBrand] = useState(false);
  const [brandedContent, setBrandedContent] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [result, setResult] = useState(null);           // {plan_id, receipt_id, ...}
  const [status, setStatus] = useState(null);           // polled status payload
  const pollRef = useRef(null);

  // ---- load connections + sources ----------------------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/publish/channel-connections?channel=tiktok"),
          api.get("/publish/tiktok/sources"),
        ]);
        if (!alive) return;
        const rows = (c.data?.connections || []).filter((r) => r.status !== "revoked");
        setConnections(rows);
        if (rows.length === 1) setConnectionId(rows[0].id);
        setSources(s.data?.sources || []);
      } catch (e) {
        if (alive) {
          setConnections([]);
          setSources([]);
          setCreatorError(formatApiError(e));
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  // ---- 1 · creator info as soon as a connection is chosen -----------------
  const loadCreator = useCallback(async (cid) => {
    setCreator(null);
    setCreatorError(null);
    setPrivacy("");
    if (!cid) return;
    try {
      const r = await api.get(`/publish/tiktok/creator-info?connection_id=${encodeURIComponent(cid)}`);
      setCreator(r.data);
      // Reset toggles the creator has turned off (they can't be enabled).
      if (r.data?.comment_disabled) setAllowComment(false);
      if (r.data?.duet_disabled) setAllowDuet(false);
      if (r.data?.stitch_disabled) setAllowStitch(false);
    } catch (e) {
      setCreatorError(formatApiError(e));
    }
  }, []);
  useEffect(() => { loadCreator(connectionId); }, [connectionId, loadCreator]);

  const source = useMemo(() => (sources || []).find((s) => s.asset_set_id === sourceId) || null, [sources, sourceId]);
  useEffect(() => { if (source && !title) setTitle(source.caption || ""); }, [source]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- 5 · branded content forbids "Only me" --------------------------------
  const brandedOn = disclosure && brandedContent;
  useEffect(() => {
    if (brandedOn && privacy === "SELF_ONLY") setPrivacy("");
  }, [brandedOn, privacy]);

  const maxDur = creator?.max_video_post_duration_sec || null;
  const tooLong = Boolean(maxDur && source?.duration_s && source.duration_s > maxDur);

  // ---- publish gating -------------------------------------------------------
  const blockers = [];
  if (!connectionId) blockers.push("Choose a TikTok account.");
  if (!creator) blockers.push("Waiting for creator info.");
  if (!source) blockers.push("Pick a video.");
  if (!privacy) blockers.push("Choose who can view this video.");
  if (disclosure && !yourBrand && !brandedContent) blockers.push(DISCLOSURE_REQUIRED_TEXT);
  if (tooLong) blockers.push(`Video is longer than the ${fmtSeconds(maxDur)} this account allows.`);
  const canPublish = blockers.length === 0 && !publishing && !result;

  const commercialLabel = disclosure
    ? (brandedContent ? "Paid partnership" : yourBrand ? "Promotional content" : null)
    : null;

  // ---- 8 · explicit click → publish → poll -----------------------------------
  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => stopPolling, []);

  async function pollOnce(receiptId) {
    try {
      const r = await api.get(`/publish/tiktok/direct-post/${receiptId}/status`);
      setStatus(r.data);
      const exec = r.data?.receipt?.execution_status;
      const tt = r.data?.tiktok_status?.status;
      if (exec === "failed" || tt === "PUBLISH_COMPLETE" || tt === "FAILED"
          || (r.data?.sent_to_inbox && tt === "SEND_TO_USER_INBOX")) stopPolling();
    } catch (e) {
      setStatus((s) => ({ ...(s || {}), poll_error: formatApiError(e) }));
    }
  }

  async function handlePublish() {
    if (!canPublish) return;
    setPublishing(true);
    setPublishError(null);
    try {
      const r = await api.post("/publish/tiktok/direct-post", {
        asset_set_id: source.asset_set_id,
        connection_id: connectionId,
        title,
        privacy_level: privacy,
        allow_comment: allowComment,
        allow_duet: allowDuet,
        allow_stitch: allowStitch,
        commercial_disclosure: disclosure,
        your_brand: disclosure && yourBrand,
        branded_content: disclosure && brandedContent,
        consent_accepted: true,     // the consent line sits directly above this button
      });
      setResult(r.data);
      pollOnce(r.data.receipt_id);
      pollRef.current = setInterval(() => pollOnce(r.data.receipt_id), POLL_MS);
    } catch (e) {
      setPublishError(formatApiError(e));
    } finally {
      setPublishing(false);
    }
  }

  function resetForm() {
    stopPolling();
    setResult(null); setStatus(null); setPublishError(null);
    setPrivacy(""); setAllowComment(false); setAllowDuet(false); setAllowStitch(false);
    setDisclosure(false); setYourBrand(false); setBrandedContent(false);
  }

  const ttStatus = status?.tiktok_status?.status;
  const execStatus = status?.receipt?.execution_status;

  return (
    <Shell title="Post to TikTok">
      <div data-testid="tiktok-direct-post-page" style={{ display: "grid", gap: 16, maxWidth: 980 }}>
        <p style={{ margin: 0, color: "var(--bo-muted)", fontSize: 14 }}>
          Publish a video from this business to its connected TikTok account. Nothing is posted until you
          click <strong>Publish to TikTok</strong>.{" "}
          <Link to="/brand-os/publish/tiktok/results" data-testid="tiktok-open-results">See TikTok results →</Link>
        </p>

        {/* 1 · account + creator info */}
        <Section step={1} title="TikTok account" testId="tiktok-step-account">
          {connections === null ? <LoadingCard label="Loading TikTok connections…" /> : connections.length === 0 ? (
            <div data-testid="tiktok-no-connection" style={{ color: "var(--bo-muted)" }}>
              No TikTok account is connected for this business yet.{" "}
              <Link to="/brand-os/admin/channel-connections">Connect TikTok →</Link>
            </div>
          ) : (
            <div className="bo-field" style={{ maxWidth: 420 }}>
              <label className="bo-label" htmlFor="tiktok-connection">Account</label>
              <select
                id="tiktok-connection" className="bo-input" value={connectionId}
                onChange={(e) => setConnectionId(e.target.value)}
                data-testid="tiktok-connection-select"
              >
                <option value="">Choose…</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>{c.display_name || c.id}</option>
                ))}
              </select>
            </div>
          )}
          <ErrorBanner error={creatorError} testId="tiktok-creator-error" />
          {connectionId && !creator && !creatorError && <LoadingCard label="Asking TikTok for the creator's settings…" />}
          {creator && (
            <div
              data-testid="tiktok-creator-card"
              style={{ display: "flex", alignItems: "center", gap: 14, padding: 12,
                       border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius)" }}
            >
              {creator.avatar_url ? (
                <img src={creator.avatar_url} alt={`${creator.nickname || "Creator"} avatar`}
                     width={56} height={56} style={{ borderRadius: "50%", objectFit: "cover" }}
                     data-testid="tiktok-creator-avatar" />
              ) : (
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "var(--bo-panel-alt)" }} />
              )}
              <div style={{ display: "grid", gap: 2 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }} data-testid="tiktok-creator-nickname">
                  {creator.nickname || creator.display_name || "TikTok creator"}
                </div>
                {creator.username && <div style={{ color: "var(--bo-muted)", fontSize: 13 }}>@{creator.username}</div>}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
                  <Pill tone={creator.direct_post_available ? "success" : "warn"} testId="tiktok-post-mode-pill">
                    {creator.direct_post_available ? "Direct post (video.publish)" : "Inbox draft (video.upload)"}
                  </Pill>
                  {maxDur && <Pill tone="muted" testId="tiktok-max-duration-pill">Max {fmtSeconds(maxDur)} per video</Pill>}
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* 7 · video + preview */}
        <Section step={2} title="Video" testId="tiktok-step-video">
          {sources === null ? <LoadingCard label="Loading approved reels…" /> : sources.length === 0 ? (
            <div style={{ color: "var(--bo-muted)" }} data-testid="tiktok-no-sources">
              No approved reels yet. Upload and approve a reel in Creative → Reels first.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0,1fr) 260px" }}>
              <div style={{ display: "grid", gap: 10 }}>
                <div className="bo-field">
                  <label className="bo-label" htmlFor="tiktok-source">Approved reel</label>
                  <select id="tiktok-source" className="bo-input" value={sourceId}
                          onChange={(e) => { setSourceId(e.target.value); setTitle(""); }}
                          data-testid="tiktok-source-select">
                    <option value="">Choose a reel…</option>
                    {sources.map((s) => (
                      <option key={s.asset_set_id} value={s.asset_set_id}>
                        {s.name || s.asset_set_id}{s.duration_s ? ` · ${fmtSeconds(s.duration_s)}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="bo-field">
                  <label className="bo-label" htmlFor="tiktok-title">Caption</label>
                  <textarea id="tiktok-title" className="bo-input" rows={3} maxLength={2200}
                            value={title} onChange={(e) => setTitle(e.target.value)}
                            data-testid="tiktok-title-input" />
                  <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>{title.length}/2200</span>
                </div>
                {tooLong && (
                  <div className="bo-login-error" role="alert" data-testid="tiktok-too-long-error">
                    This video is {fmtSeconds(source.duration_s)} long; this TikTok account allows at most{" "}
                    {fmtSeconds(maxDur)} per post. Pick a shorter cut.
                  </div>
                )}
              </div>
              <div>
                {source ? (
                  // Plain preview: no watermark, no overlay, no promotional text.
                  <video
                    key={source.video_url} src={source.video_url} controls playsInline preload="metadata"
                    poster={source.cover_url || undefined}
                    style={{ width: "100%", aspectRatio: "9 / 16", background: "#000", borderRadius: "var(--bo-radius)" }}
                    data-testid="tiktok-video-preview"
                  />
                ) : (
                  <div style={{ aspectRatio: "9 / 16", borderRadius: "var(--bo-radius)",
                                background: "var(--bo-panel-alt)", display: "grid", placeItems: "center",
                                color: "var(--bo-muted)", fontSize: 13 }}>
                    Preview appears here
                  </div>
                )}
              </div>
            </div>
          )}
        </Section>

        {/* 2 · privacy — no default */}
        <Section step={3} title="Who can view this video" testId="tiktok-step-privacy">
          {!creator ? (
            <div style={{ color: "var(--bo-muted)" }}>Options come from TikTok once the account is chosen.</div>
          ) : (
            <div role="radiogroup" aria-label="Who can view this video" style={{ display: "grid", gap: 8 }}
                 data-testid="tiktok-privacy-group">
              {(creator.privacy_level_options || []).map((o) => {
                const blocked = brandedOn && o.value === "SELF_ONLY";
                return (
                  <label key={o.value} title={blocked ? PRIVATE_BLOCKED_TEXT : undefined}
                         style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                                  border: `1px solid ${privacy === o.value ? "var(--bo-accent)" : "var(--bo-line)"}`,
                                  borderRadius: "var(--bo-radius-sm)", opacity: blocked ? 0.5 : 1,
                                  cursor: blocked ? "not-allowed" : "pointer" }}>
                    <input type="radio" name="tiktok-privacy" value={o.value}
                           checked={privacy === o.value} disabled={blocked}
                           onChange={() => setPrivacy(o.value)}
                           data-testid={`tiktok-privacy-${o.value}`}
                           style={{ accentColor: "var(--bo-accent)" }} />
                    <span style={{ fontWeight: 600 }}>{o.label}</span>
                    {blocked && <span style={{ fontSize: 12, color: "var(--bo-muted)" }}>{PRIVATE_BLOCKED_TEXT}</span>}
                  </label>
                );
              })}
              {!privacy && <div style={{ fontSize: 12, color: "var(--bo-warn)" }} data-testid="tiktok-privacy-required">
                Required — nothing is pre-selected.
              </div>}
            </div>
          )}
        </Section>

        {/* 3 · interactions — off by default, greyed when creator disabled */}
        <Section step={4} title="Allow users to" testId="tiktok-step-interactions">
          <div style={{ display: "grid", gap: 8, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <Toggle id="tt-allow-comment" label="Comment" checked={allowComment} onChange={setAllowComment}
                    disabled={!creator || creator.comment_disabled}
                    hint={creator?.comment_disabled ? "Turned off in this creator's TikTok settings" : undefined}
                    testId="tiktok-allow-comment" />
            <Toggle id="tt-allow-duet" label="Duet" checked={allowDuet} onChange={setAllowDuet}
                    disabled={!creator || creator.duet_disabled}
                    hint={creator?.duet_disabled ? "Turned off in this creator's TikTok settings" : undefined}
                    testId="tiktok-allow-duet" />
            <Toggle id="tt-allow-stitch" label="Stitch" checked={allowStitch} onChange={setAllowStitch}
                    disabled={!creator || creator.stitch_disabled}
                    hint={creator?.stitch_disabled ? "Turned off in this creator's TikTok settings" : undefined}
                    testId="tiktok-allow-stitch" />
          </div>
        </Section>

        {/* 5 · commercial content disclosure */}
        <Section step={5} title="Disclose video content" testId="tiktok-step-disclosure">
          <Toggle id="tt-disclosure" label="Your video promotes a brand, product or service"
                  checked={disclosure} onChange={(v) => { setDisclosure(v); if (!v) { setYourBrand(false); setBrandedContent(false); } }}
                  disabled={!creator} testId="tiktok-disclosure-toggle"
                  hint="Off by default. Turn on to add the disclosure label TikTok requires." />
          {disclosure && (
            <div style={{ display: "grid", gap: 8, paddingLeft: 8 }} data-testid="tiktok-disclosure-options">
              <Toggle id="tt-your-brand" label="Your brand" checked={yourBrand} onChange={setYourBrand}
                      hint="You are promoting yourself or your own business. Labelled “Promotional content”."
                      testId="tiktok-your-brand" />
              <Toggle id="tt-branded" label="Branded content" checked={brandedContent} onChange={setBrandedContent}
                      hint="You are promoting another brand or a third party. Labelled “Paid partnership”. Cannot be posted as “Only me”."
                      testId="tiktok-branded-content" />
              {commercialLabel ? (
                <div style={{ fontSize: 13 }} data-testid="tiktok-commercial-label">
                  Your video will be labelled <strong>{commercialLabel}</strong>.
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "var(--bo-warn)" }} data-testid="tiktok-disclosure-required">
                  {DISCLOSURE_REQUIRED_TEXT}
                </div>
              )}
            </div>
          )}
        </Section>

        {/* 6 · consent + 8 · explicit publish */}
        <section className="bo-card" data-testid="tiktok-step-publish" style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14 }} data-testid="tiktok-consent-line">
            By posting, you agree to TikTok's{" "}
            {brandedOn && (
              <>
                <a href={BC_POLICY_URL} target="_blank" rel="noreferrer" data-testid="tiktok-bc-policy-link">Branded Content Policy</a>
                {" and "}
              </>
            )}
            <a href={MUSIC_USAGE_URL} target="_blank" rel="noreferrer" data-testid="tiktok-music-usage-link">Music Usage Confirmation</a>.
          </p>
          <ErrorBanner error={publishError} testId="tiktok-publish-error" />
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button" className="bo-btn bo-btn-primary" onClick={handlePublish}
              disabled={!canPublish} title={blockers[0] || "Publish to TikTok"}
              data-testid="tiktok-publish-button"
            >
              {publishing ? "Publishing…" : creator && !creator.direct_post_available ? "Send to TikTok inbox" : "Publish to TikTok"}
            </button>
            {!result && blockers.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--bo-muted)" }} data-testid="tiktok-publish-blocker">{blockers[0]}</span>
            )}
            {result && (
              <button type="button" className="bo-btn bo-btn-ghost" onClick={resetForm} data-testid="tiktok-post-another">
                Post another
              </button>
            )}
          </div>

          {result && (
            <div data-testid="tiktok-publish-status" style={{ display: "grid", gap: 8, padding: 12,
                 border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Pill tone={STATUS_TONE[ttStatus] || (execStatus === "failed" ? "danger" : "warn")} testId="tiktok-status-pill">
                  {ttStatus || (execStatus === "failed" ? "FAILED" : execStatus === "published" ? "UPLOADED" : "UPLOADING…")}
                </Pill>
                <span style={{ fontSize: 13 }}>
                  {result.post_mode === "MEDIA_UPLOAD"
                    ? "Sent to the TikTok app inbox — finish posting in the TikTok app."
                    : ttStatus === "PUBLISH_COMPLETE"
                      ? "TikTok reports the post is complete."
                      : ttStatus === "FAILED" || execStatus === "failed"
                        ? (status?.receipt?.failure_envelope?.message || status?.tiktok_status?.fail_reason || "TikTok rejected the post.")
                        : "TikTok is processing the upload…"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--bo-muted)" }} data-testid="tiktok-status-note">
                {result.status_note || "Posting can take a few minutes to appear on the TikTok profile."}
                {status?.poll_error ? ` · last check failed: ${status.poll_error}` : ""}
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 13, flexWrap: "wrap" }}>
                <Link to={`/brand-os/publish/receipts/${result.receipt_id}`} data-testid="tiktok-open-receipt">Open receipt →</Link>
                {status?.receipt?.external_artifact_url && (
                  <a href={status.receipt.external_artifact_url} target="_blank" rel="noreferrer" data-testid="tiktok-open-post">
                    Open on TikTok →
                  </a>
                )}
                <Link to="/brand-os/publish/tiktok/results" data-testid="tiktok-status-results">TikTok results →</Link>
              </div>
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}
